// @flow

import { createSelector } from 'reselect';
import { getRatio } from './ratio';
import {
  getActivePdbId,
  getExtraContracts as getPdbExtraContracts,
} from '../../../pdb/selectors';
import {
  getReducer,
} from './main';
import type {
  ActivesState as State,
  ExtraContractId,
  ExtraMethods,
} from '../types';
import type { State as ConfigState } from '../../types';
import type { State as PdbState } from '../../../pdb/types';
import type { OrderFormState } from '../../../orderForm/types';
import type { ExtraContract, Ratio } from '../../../../types/pdb';

type GlobalState = {
  configuration: ConfigState,
  pdb: PdbState,
  orderForm: OrderFormState,
};

/**
 * We export the function to get all extra contracts for a car here, since we do that for
 * all actives. The reason is that some car properties depend on choices of other
 * car properties.
 */
export const makeGetExtraContracts = (pdbId?: ?string) => (
  createSelector<GlobalState, ?string, Array<ExtraContract>, Ratio, Array<ExtraContract>>(
    [
      (state: GlobalState) => getRatio(state, pdbId),
      (state: GlobalState) => getPdbExtraContracts(state, pdbId),
    ],
    (ratio: Ratio, pdbExtraContracts: Array<ExtraContract>): Array<ExtraContract> => (
      pdbExtraContracts.map((extra: ExtraContract) => ({
        ...extra,
        price: extra.recalculate ? (parseFloat(extra.price) * ratio).toString() : extra.price,
      }))
    ),
  )
);

export const getExtraContracts = (state: GlobalState, pdbId?: ?string) => (
  makeGetExtraContracts(pdbId)(state)
);

type ActiveExtraContracts = $PropertyType<State, 'activeExtraContracts'>;
export const getExtraContractsFromActives = (state: GlobalState, pdbId?: ?string) => {
  const selectedPdbId = pdbId || getActivePdbId(state);
  const reducer: ActiveExtraContracts = getReducer(state).activeExtraContracts;
  return reducer[selectedPdbId] || {};
};

// @TODO: This code is duplicated in accessories.js
export const getMethodsForActiveExtraContract = (
  state: GlobalState,
  extraContractId: ExtraContractId,
): string[] => {
  const extraContractsByMethod = getExtraContractsFromActives(state);

  return Object.entries(extraContractsByMethod)
    .reduce(
      (listOfMethods, [method, extraContractsForMethod]) => {
        if (
          !Array.isArray(extraContractsForMethod)
            || !extraContractsForMethod.includes(extraContractId)
        ) {
          // ExtraContract is not an array of ids, so exit early
          return listOfMethods;
        }

        return [
          ...listOfMethods,
          method,
        ];
      },
      [],
    );
};

// @TODO: This code is duplicated in accessories.js
export const getActiveExtraContractsIds = (state: GlobalState): ExtraContractId[] => {
  const extraContractsByMethod = getExtraContractsFromActives(state);

  const idSet = Object.values(extraContractsByMethod)
    .reduce(
      (extraContractIds, extraContractsForMethod) => {
        if (!Array.isArray(extraContractsForMethod)) {
          // Accessories is not an array of ids, so exit early
          return extraContractIds;
        }

        return [
          ...extraContractIds,
          ...extraContractsForMethod.map(id => parseInt(id, 10)),
        ];
      },
      [],
    );

  return Array.from(new Set(idSet));
};

// @TODO: This code is duplicated in accessories.js
export const getActiveExtraContracts = (state: GlobalState): ExtraContract[] => {
  const extraContracts = getExtraContracts(state) || [];
  const activeIds = getActiveExtraContractsIds(state);
  return extraContracts.filter(x => activeIds.includes(x.id));
};

// @TODO: This code is duplicated in accessories.js
export const getActiveExtraContractsByMethod = (
  state: GlobalState,
  method: ExtraMethods,
  exclusive: ?boolean = false,
  pdbId?: ?string,
): ExtraContract[] => {
  const extraContracts = getExtraContracts(state, pdbId) || [];
  const activeExtraContracts = getExtraContractsFromActives(state, pdbId);
  let extraContractsForMethod = activeExtraContracts[method];

  if (!extraContractsForMethod || !extraContractsForMethod.length) {
    return [];
  }

  if (exclusive) {
    // Exclusive: active can only exist in one method
    extraContractsForMethod = Object.entries(activeExtraContracts)
      .reduce(
        (uniqueActiveECs, [ecMethod, ecActives]) => {
          if (ecMethod === method) {
            return uniqueActiveECs;
          }

          return uniqueActiveECs.filter(id => (
            !Array.isArray(ecActives) || !ecActives.includes(id)
          ));
        },
        extraContractsForMethod,
      );
  }

  return extraContracts.filter(x => extraContractsForMethod.includes(x.id));
};
export const getActiveNonRecurringContracts = (
  state: GlobalState,
  method: ExtraMethods,
  exclusive: ?boolean = false,
  pdbId?: ?string,
): ExtraContract[] => getActiveExtraContractsByMethod(state, 'selected', exclusive, pdbId)
  .filter(contract => contract.recurring);

// @TODO: This code is duplicated in accessories.js
export const getActiveExtraContractsIdsByMethod = (
  state: GlobalState,
  method: ExtraMethods,
  exclusive: ?boolean = false,
): number[] => getActiveExtraContractsByMethod(state, method, exclusive)
  .map(extraContract => extraContract.id);

export const getExtraContractSelectedById = (
  state: GlobalState,
  extraContractId: ExtraContractId,
): boolean => {
  const activePdbId = getActivePdbId(state);
  if (!activePdbId) {
    return false;
  }
  const activeExtraContracts = getExtraContractsFromActives(state, activePdbId);
  if (activeExtraContracts.selected
    && activeExtraContracts.selected.includes(extraContractId)) {
    return true;
  }
  return false;
};

export default null;
